/**
 * Task Board Component
 * Stacked bar chart view - projects as horizontal bars, tasks stacked by priority
 * Task segment width is proportional to the task name length (characters)
 */

import React, { useState } from "react";
import { Typography, Empty, Spin, Tooltip, Popover } from "antd";
import { TaskCard, TaskCardData } from "./TaskCard";

const { Text } = Typography;

export interface ProjectRow {
  id: string;
  name: string;
  color?: string;
  isActive: boolean;
  tasks: TaskCardData[];
}

export interface TaskBoardProps {
  projects: ProjectRow[];
  isLoading?: boolean;
  selectedContexts?: string[];
  onTaskClick?: (taskId: string) => void;
  onTaskUpdate?: () => void;
}

// Priority lanes configuration (left to right = most urgent to least)
const PRIORITY_LANES = [
  { key: "now", label: "NOW", color: "#ff4d4f" },
  { key: "tomorrow", label: "TOMORROW", color: "#faad14" },
  { key: "this_week", label: "THIS WEEK", color: "#1890ff" },
  { key: "next_week", label: "NEXT WEEK+", color: "#8c8c8c" },
  { key: "someday", label: "SOMEDAY", color: "#e8e8e8" },
];

export const TaskBoard: React.FC<TaskBoardProps> = ({
  projects,
  isLoading = false,
  selectedContexts = [],
  onTaskClick,
  onTaskUpdate,
}) => {
  const [expandedProject, setExpandedProject] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <Spin />
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <Empty
        description="Nessun progetto attivo"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  // Filter tasks by selected contexts
  const filterTasks = (tasks: TaskCardData[]) => {
    if (selectedContexts.length === 0) return tasks;
    return tasks.filter(
      (t) => !t.context || selectedContexts.includes(t.context)
    );
  };

  // Get tasks grouped by priority for a project
  const getTasksByPriority = (tasks: TaskCardData[]) => {
    const filtered = filterTasks(tasks);
    const grouped: Record<string, TaskCardData[]> = {};
    PRIORITY_LANES.forEach((lane) => {
      grouped[lane.key] = filtered.filter((t) => t.priority === lane.key);
    });
    return grouped;
  };

  // Sort projects by number of tasks (descending)
  const sortedProjects = [...projects].sort((a, b) => {
    const aTasks = filterTasks(a.tasks).length;
    const bTasks = filterTasks(b.tasks).length;
    return bTasks - aTasks;
  });

  // Calculate total tasks for percentage width
  const maxTasks = Math.max(
    ...projects.map((p) => filterTasks(p.tasks).length),
    1
  );

  return (
    <div style={{ padding: "8px 0" }}>
      {/* Legend */}
      <div
        style={{
          display: "flex",
          gap: 16,
          marginBottom: 16,
          padding: "8px 12px",
          background: "#fafafa",
          borderRadius: 4,
          flexWrap: "wrap",
        }}
      >
        {PRIORITY_LANES.map((lane) => (
          <div
            key={lane.key}
            style={{ display: "flex", alignItems: "center", gap: 4 }}
          >
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: 2,
                background: lane.color,
              }}
            />
            <Text style={{ fontSize: 11 }}>{lane.label}</Text>
          </div>
        ))}
      </div>

      {/* Project Bars */}
      {sortedProjects
        .filter((p) => p.isActive)
        .map((project) => {
          const tasksByPriority = getTasksByPriority(project.tasks);
          const totalTasks = filterTasks(project.tasks).length;
          const barWidth = totalTasks > 0 ? (totalTasks / maxTasks) * 100 : 5;

          return (
            <div
              key={project.id}
              style={{
                marginBottom: 12,
                cursor: "pointer",
              }}
              onClick={() =>
                setExpandedProject(
                  expandedProject === project.id ? null : project.id
                )
              }
            >
              {/* Project Label */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 4,
                }}
              >
                <Text
                  strong
                  style={{
                    fontSize: 12,
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {project.name}
                </Text>
                <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
                  {totalTasks} task{totalTasks !== 1 ? "s" : ""}
                </Text>
              </div>

              {/* Stacked Bar - each task segment proportional to task name length */}
              <div
                style={{
                  display: "flex",
                  height: 28,
                  width: `${Math.max(barWidth, 10)}%`,
                  minWidth: 60,
                  borderRadius: 4,
                  overflow: "hidden",
                  background: "#f5f5f5",
                  border: "1px solid #e8e8e8",
                }}
              >
                {(() => {
                  // Get all tasks sorted by priority order
                  const allTasks: {
                    task: TaskCardData;
                    lane: (typeof PRIORITY_LANES)[0];
                  }[] = [];
                  PRIORITY_LANES.forEach((lane) => {
                    tasksByPriority[lane.key].forEach((task) => {
                      allTasks.push({ task, lane });
                    });
                  });

                  // Calculate total character length for proportional widths
                  const totalChars = allTasks.reduce(
                    (sum, { task }) => sum + (task.name?.length || 5),
                    0
                  );

                  // Mixed formula constants
                  const MIN_WIDTH_PER_CHAR = 0.5; // Minimum 0.5% per character (absolute)
                  const RELATIVE_WEIGHT = 0.5; // 50% relative, 50% absolute

                  return allTasks.map(({ task, lane }) => {
                    const nameLength = task.name?.length || 5;
                    // Relative component: based on proportion of total
                    const relativeWidth =
                      totalChars > 0 ? (nameLength / totalChars) * 100 : 0;
                    // Absolute component: based on character count directly
                    const absoluteWidth = nameLength * MIN_WIDTH_PER_CHAR;
                    // Mixed formula: 70% relative + 30% absolute
                    const segmentWidth =
                      RELATIVE_WEIGHT * relativeWidth +
                      (1 - RELATIVE_WEIGHT) * absoluteWidth;

                    return (
                      <Popover
                        key={task.id}
                        content={
                          <div style={{ maxWidth: 300 }}>
                            <TaskCard
                              task={task}
                              compact
                              onClick={
                                onTaskClick
                                  ? () => onTaskClick(task.id)
                                  : undefined
                              }
                              onUpdate={onTaskUpdate}
                            />
                          </div>
                        }
                        title={null}
                        trigger="click"
                        placement="bottom"
                      >
                        <Tooltip title={task.name}>
                          <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              width: `${segmentWidth}%`,
                              minWidth: 8,
                              height: "100%",
                              background: lane.color,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              transition: "filter 0.2s",
                              borderRight: "1px solid rgba(255,255,255,0.3)",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.filter = "brightness(1.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.filter = "brightness(1)";
                            }}
                          >
                            <Text
                              style={{
                                color: lane.key === "someday" ? "#666" : "#fff",
                                fontSize: 9,
                                fontWeight: 500,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                padding: "0 2px",
                                maxWidth: "100%",
                              }}
                            >
                              {nameLength > 15
                                ? task.name?.substring(0, 12) + "..."
                                : task.name}
                            </Text>
                          </div>
                        </Tooltip>
                      </Popover>
                    );
                  });
                })()}
              </div>

              {/* Expanded Task List */}
              {expandedProject === project.id && (
                <div
                  style={{
                    marginTop: 8,
                    padding: 12,
                    background: "#fafafa",
                    borderRadius: 4,
                    border: "1px solid #e8e8e8",
                  }}
                >
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(200px, 1fr))",
                      gap: 8,
                    }}
                  >
                    {PRIORITY_LANES.map((lane) => {
                      const laneTasks = tasksByPriority[lane.key];
                      if (laneTasks.length === 0) return null;

                      return (
                        <div key={lane.key}>
                          <Text
                            strong
                            style={{
                              fontSize: 11,
                              color: lane.color,
                              display: "block",
                              marginBottom: 4,
                            }}
                          >
                            {lane.label} ({laneTasks.length})
                          </Text>
                          {laneTasks.map((task) => (
                            <TaskCard
                              key={task.id}
                              task={task}
                              compact
                              onClick={
                                onTaskClick
                                  ? () => onTaskClick(task.id)
                                  : undefined
                              }
                              onUpdate={onTaskUpdate}
                            />
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}

      {/* Inactive Projects */}
      {sortedProjects.filter((p) => !p.isActive).length > 0 && (
        <div style={{ marginTop: 24, opacity: 0.6 }}>
          <Text
            type="secondary"
            style={{ fontSize: 11, marginBottom: 8, display: "block" }}
          >
            PROGETTI INATTIVI
          </Text>
          {sortedProjects
            .filter((p) => !p.isActive)
            .map((project) => {
              const totalTasks = filterTasks(project.tasks).length;
              return (
                <div
                  key={project.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 4,
                  }}
                >
                  <Text style={{ fontSize: 12 }}>{project.name}</Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    ({totalTasks})
                  </Text>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};
